import { thousandSeparated } from 'shared/util/formatting';
import { ObjectPriceRange } from '@/interfaces';

export const priceDescription = {
    day: 'Pris per natt',
    week: 'Pris per uke',
    weekend: 'Pris per helg',
    year: 'Tilbyr åremål/langtidsleie',
};
export const additionalDescription = {
    currency: 'Pris oppgitt i',
    depositType: 'Depositum',
    cleaning: 'Rengjøringsgebyr',
    linens: 'Håndklær og sengetøy',
};

export const isPriceRange = (priceRange: unknown): boolean =>
    priceRange && (Object.prototype.hasOwnProperty.call(priceRange, 'from') || Object.prototype.hasOwnProperty.call(priceRange, 'to'));

export const hasPriceRangePrices = (priceRange: ObjectPriceRange): boolean => Boolean(priceRange?.from || priceRange?.to);

export const _formatNumber = (num: number, currencySymbol: string): string | null =>
    num ? `${thousandSeparated(num) + currencySymbol}` : null;

export const _formatPercent = (num: number): string | null => (num ? `${num}%` : null);

export const _formatRange = (priceRange: ObjectPriceRange, currencySymbol: string): string => {
    if (priceRange.from === priceRange.to) {
        return _formatNumber(priceRange?.from, currencySymbol);
    }

    if (priceRange.from && priceRange.to) {
        return `fra ${_formatNumber(priceRange.from, currencySymbol)} til ${_formatNumber(priceRange.to, currencySymbol)}`;
    }

    return (
        (priceRange.from ? _formatNumber(priceRange.from, currencySymbol) : '') +
        (priceRange.to ? _formatNumber(priceRange.to, currencySymbol) : '')
    );
};

export const resolveCurrencySuffix = (currencyCode: string) => {
    switch (currencyCode) {
        case 'NOK':
            return ',-';
        case 'EUR':
            return ' €';
        default:
            return ',-';
    }
};

export const _formatValue = (value: unknown, key: string, pricing: Record<string, unknown>) => {
    const currencySymbol = resolveCurrencySuffix(pricing?.currency?.toString());

    switch (true) {
        case 'year' === key:
            return '';
        case isPriceRange(value):
            if (!hasPriceRangePrices(value as ObjectPriceRange)) {
                return null;
            }

            return _formatRange(value as ObjectPriceRange, currencySymbol);
        case 'cleaning' === key || 'linens' === key:
            return _formatNumber(Number(value), currencySymbol);
        case 'depositType' === key && 'NONE' === value:
            return null;
        case 'depositType' === key && 'REQUIRED' === value:
            return 'Ja';
        case 'depositType' === key && 'FIXED' === value:
            return _formatNumber(Number(pricing.depositAmount), currencySymbol);
        case 'depositType' === key && 'PERCENT' === value:
            return _formatPercent(Number(pricing.depositAmount));
        default:
            return value;
    }
};

export const generateEntries = (possibleDescriptions: Record<string, unknown>, pricing?: Record<string, unknown>) => {
    if (pricing === undefined) {
        return [];
    }

    return Object.keys(possibleDescriptions).reduce((entries, key) => {
        if (Object.prototype.hasOwnProperty.call(pricing, key)) {
            const pricingItemValue = pricing[key];
            const description = possibleDescriptions[key];
            const value = _formatValue(pricingItemValue, key, pricing);

            if (value) {
                entries.push({ key, value, description });
            }
        }
        return entries;
    }, []);
};
